import { OrderCard } from "./ToolsMobileCards";
import { useToolsMobile } from "./ToolsLayout";
import React from "react";
import {
  Alert,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Add, ArrowForward } from "@mui/icons-material";
import { Link } from "react-router-dom";
import ToolsLayout, { MarketStatus, RefreshTools, ToolsState } from "./ToolsLayout";
import useToolsData from "./useToolsData";
import { money, orderTotal, stockState, summarizeMarket, toolsDate } from "./toolsData";

export default function ToolsMarket() {
  const mobile = useToolsMobile();
  const products = useToolsData("products"),
    orders = useToolsData("orders"),
    payments = useToolsData("payments");
  const stats = summarizeMarket(products.data || [], orders.data || [], payments.data || []);
  const refresh = () => Promise.all([products.refetch(), orders.refetch(), payments.refetch()]);
  const metrics = [
    {
      title: "Products in catalogue",
      value: stats.products,
      detail:
        stats.units === null
          ? "Some stock quantities are unavailable"
          : `${stats.units.toLocaleString("en-IN")} units in stock`,
      query: products,
      link: "/home/tools",
      action: "Manage inventory",
    },
    {
      title: "Orders received",
      value: stats.orders,
      detail: `${stats.pending} awaiting approval · all time`,
      query: orders,
      link: "/home/tools-orders",
      action: "View orders",
    },
    {
      title: "Paid order value",
      value: money(stats.collected),
      detail: `${stats.paidOrders} paid orders · excludes cancelled / rejected`,
      query: orders,
      link: "/home/tools-payments?paymentStatus=paid",
      action: "View payments",
    },
    {
      title: "Receipts to review",
      value: stats.receipts,
      detail: "Uploaded receipts awaiting payment review",
      query: payments,
      link: "/home/tools-payments?review=1",
      action: "Review receipts",
    },
  ];
  const recent = [...(orders.data || [])]
    .sort((a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0))
    .slice(0, 5);
  const attention = (products.data || [])
    .filter((row) => ["out", "low"].includes(stockState(row.stock)))
    .sort((a, b) => Number(a.stock) - Number(b.stock))
    .slice(0, 5);
  const max = Math.max(1, ...stats.months.map((month) => month.orders));
  const updated = Math.min(
    ...[products, orders, payments].map((query) => query.dataUpdatedAt || 0),
  );
  return (
    <ToolsLayout
      title="Tools Market overview"
      description="Your catalogue, fulfilment queue, and payment reviews in one place."
      actions={
        <>
          <RefreshTools
            onClick={refresh}
            loading={[products, orders, payments].some((query) => query.isFetching)}
          />
          <Button variant="contained" startIcon={<Add />} component={Link} to="/home/add-tool">
            Add product
          </Button>
        </>
      }
    >
      {[products, orders, payments].some((query) => query.isError) && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button onClick={refresh} color="inherit">
              Retry
            </Button>
          }
        >
          Some market data is unavailable. Figures for those sections are hidden until they can be
          loaded.
        </Alert>
      )}
      <section className="tools-metrics" aria-label="Market totals">
        {metrics.map((metric) => (
          <div className="tools-metric" key={metric.title}>
            <span>{metric.title}</span>
            <strong>
              {metric.query.isLoading ? (
                <Skeleton width="65%" />
              ) : metric.query.isError ? (
                "—"
              ) : (
                metric.value
              )}
            </strong>
            <p>
              {metric.query.isError
                ? "Data unavailable"
                : metric.query.isLoading
                  ? "Loading market records…"
                  : metric.detail}
            </p>
            <Link to={metric.link}>{metric.action} →</Link>
          </div>
        ))}
      </section>
      <div className="tools-two-column">
        <section className="tools-panel">
          <div className="tools-panel-heading">
            <div>
              <Typography variant="h6" component="h2">
                Order activity
              </Typography>
              <p>Orders placed per month · last six months · IST</p>
            </div>
          </div>
          <ToolsState
            loading={orders.isLoading}
            error={orders.isError}
            retry={orders.refetch}
            empty={!stats.months.some((month) => month.orders)}
            emptyTitle="No orders in the last six months"
            emptyMessage="Monthly activity will appear as orders are placed."
          >
            <div
              className="tools-bars"
              role="img"
              aria-label={stats.months
                .map((month) => `${month.key}: ${month.orders} orders`)
                .join(", ")}
            >
              {stats.months.map((month) => (
                <div className="tools-bar" key={month.key}>
                  <strong>{month.orders}</strong>
                  <i
                    style={{
                      height: `${Math.max(month.orders ? 4 : 0, (month.orders / max) * 130)}px`,
                    }}
                  />
                  <span>{month.label}</span>
                </div>
              ))}
            </div>
          </ToolsState>
        </section>
        <section className="tools-panel">
          <div className="tools-panel-heading">
            <div>
              <Typography variant="h6" component="h2">
                Needs your attention
              </Typography>
              <p>Go straight to the work waiting for you.</p>
            </div>
          </div>
          {[
            {
              title: "Approve new orders",
              detail: "Review items and confirm shipping charges.",
              count: stats.pending,
              query: orders,
              to: "/home/tools-orders?status=pending",
            },
            {
              title: "Review payment receipts",
              detail: "Verify evidence and transaction details.",
              count: stats.receipts,
              query: payments,
              to: "/home/tools-payments?review=1",
            },
            {
              title: "Replenish inventory",
              detail: "Out of stock or 10 units or fewer.",
              count: stats.low + stats.out,
              query: products,
              to: "/home/tools?stock=attention",
            },
          ].map((task) => (
            <div className="tools-task" key={task.title}>
              <div>
                <Typography variant="body2" fontWeight={800}>
                  {task.title}
                </Typography>
                <p>{task.detail}</p>
              </div>
              <Button component={Link} to={task.to} endIcon={<ArrowForward fontSize="small" />}>
                {task.query.isLoading ? "Loading…" : task.query.isError ? "Open" : task.count}
              </Button>
            </div>
          ))}
        </section>
      </div>
      <div className="tools-two-column">
        <section className="tools-panel">
          <div className="tools-panel-heading">
            <Typography component="h2" variant="h6">
              Recent orders
            </Typography>
            <Button component={Link} to="/home/tools-orders" size="small">
              View all
            </Button>
          </div>
          <ToolsState
            loading={orders.isLoading}
            error={orders.isError}
            retry={orders.refetch}
            empty={!recent.length}
            emptyTitle="No orders yet"
          >
            {mobile ? (
              <div>
                {recent.map((order) => (
                  <OrderCard key={order._id || order.orderId} order={order} compact />
                ))}
              </div>
            ) : (
              <TableContainer>
                <Table aria-label="Recent tool orders">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order / Customer</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recent.map((order) => (
                      <TableRow key={order._id || order.orderId}>
                        <TableCell>
                          <Button
                            component={Link}
                            to={`/home/tools-order/${encodeURIComponent(order.orderId)}`}
                            size="small"
                          >
                            {order.orderId}
                          </Button>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {order.shippingAddress?.fullName || "Customer unavailable"}
                          </Typography>
                        </TableCell>
                        <TableCell>{toolsDate(order.createdOn)}</TableCell>
                        <TableCell>
                          <MarketStatus status={order.orderStatus} />
                        </TableCell>
                        <TableCell align="right">{money(orderTotal(order))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </ToolsState>
        </section>
        <section className="tools-panel">
          <div className="tools-panel-heading">
            <div>
              <Typography component="h2" variant="h6">
                Stock watch
              </Typography>
              <p>Low stock threshold: 10 units</p>
            </div>
            <Button size="small" component={Link} to="/home/tools?stock=attention">
              Inventory
            </Button>
          </div>
          <ToolsState
            loading={products.isLoading}
            error={products.isError}
            retry={products.refetch}
            empty={!attention.length}
            emptyTitle={stats.products ? "No low-stock products" : "Build your catalogue"}
            emptyMessage={
              stats.products
                ? "No products with known stock are low or out of stock."
                : "Add your first product and its opening stock."
            }
            action={
              !stats.products && (
                <Button component={Link} to="/home/add-tool">
                  Add product
                </Button>
              )
            }
          >
            {attention.map((product) => (
              <div className="tools-task" key={product._id}>
                <div>
                  <Typography variant="body2" fontWeight={800}>
                    {product.name}
                  </Typography>
                  <p>
                    {Number(product.stock) === 0
                      ? "Out of stock"
                      : `${product.stock} units remaining`}
                  </p>
                </div>
                <Button
                  size="small"
                  component={Link}
                  to={`/home/tools?product=${encodeURIComponent(product._id)}`}
                >
                  Update stock
                </Button>
              </div>
            ))}
          </ToolsState>
        </section>
      </div>
      <Typography variant="caption" color="text.secondary">
        {updated
          ? `Last synced ${new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit" }).format(new Date(updated))} IST. `
          : ""}
        Totals use all available market records. Paid order value reflects orders marked paid,
        including tax and shipping.
      </Typography>
    </ToolsLayout>
  );
}
